/**
 * ============================================================================
 * PostActionRow — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: a single <tr> in the post-action table. FAILED rows get a "Retry"
 *          button that re-enqueues via the retry mutation. The retry call is
 *          POSTed with a fresh idempotency key per CCIE-OPS-009 so a network
 *          timeout never causes a double-trigger.
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './PostActionTracker.module.css';
import type { PostAction, PostActionStatus } from './usePostActionsQuery';
import { useRetryPostActionMutation } from './useRetryPostActionMutation';

export interface PostActionRowProps {
  changeId: string;
  action: PostAction;
  onRetried: () => void;
}

const STATUS_CLASS: Record<PostActionStatus, string> = {
  QUEUED: styles.statusQueued,
  RUNNING: styles.statusRunning,
  COMPLETED: styles.statusCompleted,
  FAILED: styles.statusFailed,
};

export function PostActionRow({ changeId, action, onRetried }: PostActionRowProps) {
  const { t, i18n } = useTranslation();
  const retryM = useRetryPostActionMutation();

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const nf = new Intl.NumberFormat(i18n.language);

  const statusLabel = t(`changeControl.postActions.status.${action.status}.label`, {
    defaultValue: action.status,
  });
  const typeLabel = t(`changeControl.postActions.actionType.${action.action_type}`, {
    defaultValue: action.action_type,
  });

  async function onRetry() {
    try {
      await retryM.mutateAsync({ changeId, actionId: action.action_id });
      onRetried();
    } catch {
      // Errors surface via retryM.isError and the inline retryFailed cell.
    }
  }

  return (
    <tr>
      <td>
        <div className={styles.actionCell}>
          <code className={styles.actionType}>{typeLabel}</code>
        </div>
      </td>
      <td>
        <div className={styles.targetCell}>
          <span className={styles.targetKind} aria-hidden="true">
            {action.target_entity_kind}
          </span>
          <strong>{action.target_display_name}</strong>
        </div>
      </td>
      <td>
        <span
          className={`${styles.statusBadge} ${STATUS_CLASS[action.status]}`}
          aria-label={t('changeControl.postActions.statusAria', {
            defaultValue: 'Status: {{status}}',
            status: statusLabel,
          })}
        >
          {statusLabel}
        </span>
      </td>
      <td>
        <time dateTime={action.queued_at_utc}>
          {dateFmt.format(new Date(action.queued_at_utc))}
        </time>
      </td>
      <td>
        {action.completed_at_utc ? (
          <time dateTime={action.completed_at_utc}>
            {dateFmt.format(new Date(action.completed_at_utc))}
          </time>
        ) : (
          <span className={styles.dash} aria-label={t('changeControl.postActions.notCompleted', { defaultValue: 'Not completed' })}>
            —
          </span>
        )}
      </td>
      <td>{nf.format(action.retry_count)}</td>
      <td>
        {action.status === 'FAILED' && (
          <div className={styles.retryGroup}>
            <Button
              variant="secondary"
              size="sm"
              onClick={onRetry}
              isLoading={retryM.isPending}
              aria-label={t('changeControl.postActions.actions.retryAria', {
                defaultValue: 'Retry the {{type}} action for {{target}}',
                type: typeLabel,
                target: action.target_display_name,
              })}
            >
              {t('changeControl.postActions.actions.retry', {
                defaultValue: 'Retry',
              })}
            </Button>
            {action.failure_reason && (
              <span className={styles.failureReason} title={action.failure_reason}>
                {action.failure_reason}
              </span>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
